import { useState, useCallback, useMemo } from 'react';

import { useGetStockValuation, LedgerFetchFilter, AccountListItem } from '../../api/accounts';
import { useAppContext } from '../../contexts/Storage';
import { useCurrency } from '../../utils/globalCurrency';

interface UseStockValuationOptions {
  recordId?: number;
}

const useStockValuation = (options: UseStockValuationOptions = {}) => {
  const { recordId: initialRecordId } = options;
  const { user, selectedRecord } = useAppContext();

  // Use provided recordId or fall back to selected record
  const recordId = initialRecordId ?? selectedRecord?.id;
  const userId = user?.id;

  // Filter state
  const [filters, setFilters] = useState<Partial<LedgerFetchFilter>>({
    per_page: 15,
    page: 1,
  });

  // Search state (client-side filtering)
  const [searchTerm, setSearchTerm] = useState('');

  // Selected stock for detail view
  const [selectedStock, setSelectedStock] = useState<AccountListItem | null>(null);

  // API hook - only fetch when we have record_id and user_id
  const hasRequiredParams = !!recordId && !!userId;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetStockValuation({
    filters: {
      ...filters,
      record_id: recordId,
      user_id: userId,
    },
    enabled: hasRequiredParams,
  });

  // Derived data
  const stocks = useMemo(() => data?.stocks ?? [], [data?.stocks]);
  const pagination = useMemo(() => data?.pagination, [data?.pagination]);

  // Client-side filtered stocks (for search)
  const filteredStocks = useMemo(() => {
    if (!searchTerm.trim()) return stocks;

    const term = searchTerm.toLowerCase();
    return stocks.filter(
      (stock) =>
        stock.transaction_entity_name?.toLowerCase().includes(term) ||
        stock.transaction_entity_description?.toLowerCase().includes(term) ||
        stock.transaction_entity_category?.toLowerCase().includes(term)
    );
  }, [stocks, searchTerm]);

  // Filter handlers
  const updateFilter = useCallback((updates: Partial<LedgerFetchFilter>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleDateFilter = useCallback((startDate?: string, endDate?: string) => {
    updateFilter({
      start_date: startDate,
      end_date: endDate,
      page: 1,
    });
  }, [updateFilter]);

  const handlePageChange = useCallback((page: number) => {
    updateFilter({ page });
  }, [updateFilter]);

  const handlePerPageChange = useCallback((perPage: number) => {
    updateFilter({ per_page: perPage, page: 1 });
  }, [updateFilter]);

  const resetFilters = useCallback(() => {
    setFilters({ per_page: 15, page: 1 });
    setSearchTerm('');
  }, []);

  // Stock selection
  const handleSelectStock = useCallback((stock: AccountListItem) => {
    setSelectedStock(stock);
  }, []);

  const handleCloseStockDetail = useCallback(() => {
    setSelectedStock(null);
  }, []);

  // Get currency formatting
  const { formatWhole } = useCurrency();

  // Map stocks to ListItem format for the template
  const listItems = useMemo(() => {
    return filteredStocks.map((stock) => ({
      id: stock.id ?? 0,
      name: stock.transaction_entity_name ?? '',
      description: stock.transaction_entity_description ?? '',
      category: stock.transaction_entity_category ?? '',
      type: stock.transaction_entity_type ?? '',
      balance: stock.transaction_entity_balance_amount !== undefined
        ? formatWhole(stock.transaction_entity_balance_amount)
        : undefined,
      balanceType: stock.transaction_entity_balance_type,
      balanceQuantity: stock.transaction_entity_balance_quantity,
    }));
  }, [filteredStocks, formatWhole]);

  return {
    // Data
    stocks,
    filteredStocks,
    listItems,
    pagination,
    selectedStock,

    // Loading states
    isLoading,
    hasRequiredParams,

    // Error state
    error,

    // Filter state and handlers
    filters,
    searchTerm,
    handleSearchChange,
    handleDateFilter,
    handlePageChange,
    handlePerPageChange,
    resetFilters,
    updateFilter,

    // Stock actions
    handleSelectStock,
    handleCloseStockDetail,

    // Refetch
    refetch,
  };
};

export default useStockValuation;
